import React, { useState, useMemo, useEffect } from "react";
import { Card, Checkbox, Tooltip } from "antd";
import {
  FileExcelOutlined,
  InfoCircleOutlined,
  FilterOutlined,
  CalendarOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import {
  useGetReportModulesQuery,
  useGetReportSchemaQuery,
  useDownloadReportMutation,
} from "../../store/apis/dynamicReports.api";
import { useGetMasterConfigByCategoryQuery } from "../../store/apis/masterConfig.api";
import { useGetUsersForFilterQuery } from "../../store/apis/user.api";
import { Button, Select, DatePicker } from "../common";
import toast from "react-hot-toast";
import dayjs from "dayjs";
import { DynamicReportsSkeleton } from "./DynamicReportsSkeleton";

const DynamicReports: React.FC = () => {
  const [selectedModule, setSelectedModule] = useState<string | undefined>(undefined);
  const [downloadReport, { isLoading: downloading }] = useDownloadReportMutation();

  // Form State
  const [filters, setFilters] = useState<Record<string, any>>({
    startDate: null,
    endDate: null,
    monthDate: null,
    department: "",
    employeeId: "",
  });

  // Load modules list
  const { data: modules = [], isLoading: loadingModules } = useGetReportModulesQuery();

  // Load selected module schema
  const {
    data: schema,
    isLoading: loadingSchema,
    isFetching: fetchingSchema,
    error: schemaError,
  } = useGetReportSchemaQuery(selectedModule || "", {
    skip: !selectedModule,
  });

  const isSchemaLoading = (loadingSchema || fetchingSchema) && Boolean(selectedModule);

  // Load lookups for standard pickers
  const { data: departments = [] } = useGetMasterConfigByCategoryQuery("department");
  const { data: employees = [] } = useGetUsersForFilterQuery();

  // Selected Columns State
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  // Trigger toast error if schema loading fails
  useEffect(() => {
    if (schemaError) {
      toast.error("Failed to load module schema metadata.");
    }
  }, [schemaError]);

  const placeholderColumns = useMemo(() => [
    { key: "fullName", label: "Employee Name", defaultVisible: true, locked: true },
    { key: "employeeId", label: "Employee ID", defaultVisible: true },
    { key: "departmentName", label: "Department", defaultVisible: true },
  ], []);

  const columnsToRender = useMemo(() => schema?.columns || placeholderColumns, [schema, placeholderColumns]);

  const visibleColumns = useMemo(() => {
    return columnsToRender.filter((col) => !col.key.startsWith("day_"));
  }, [columnsToRender]);

  const isFiltersValid = useMemo(() => {
    if (!schema) return false;
    if (filters.startDate && filters.endDate) {
      if (dayjs(filters.startDate).isAfter(dayjs(filters.endDate))) return false;
    }
    return true;
  }, [schema, filters]);

  // Initialize selected columns when schema loads
  useEffect(() => {
    const cols = schema?.columns || placeholderColumns;
    const defaultCols = cols
      .filter((col) => col.defaultVisible && !col.key.startsWith("day_"))
      .map((col) => col.key);
    setSelectedColumns(defaultCols);
  }, [schema, placeholderColumns]);

  const handleModuleChange = (value: string) => {
    setSelectedModule(value);
    setFilters({
      startDate: null,
      endDate: null,
      monthDate: null,
      department: "",
      employeeId: "",
      employeeIds: [],
    });
  };

  const handleFilterChange = (key: string, value: any) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleColumnChange = (checkedValues: any[]) => {
    const activeCols = schema?.columns || placeholderColumns;
    const visibleActiveCols = activeCols.filter((c) => !c.key.startsWith("day_"));
    const lockedCols = visibleActiveCols.filter((c) => c.locked).map((c) => c.key);
    const merged = Array.from(new Set([...lockedCols, ...checkedValues]));
    setSelectedColumns(merged);
  };

  // Convert models list for Custom common Select component
  const moduleOptions = useMemo(() => {
    return modules.map((mod) => ({
      value: mod.moduleId,
      label: mod.name,
    }));
  }, [modules]);

  const departmentOptions = useMemo(() => {
    return [
      { value: "", label: "All Departments" },
      ...departments.map((dept) => ({ value: dept.id, label: dept.displayName })),
    ];
  }, [departments]);

  const employeeOptions = useMemo(() => {
    return employees.map((emp) => ({ value: emp.id, label: emp.fullName }));
  }, [employees]);

  const handleDownload = async () => {
    if (!selectedModule || !schema) return;

    // Validate timeline constraints: if both provided, ensure start is not after end
    if (filters.startDate && filters.endDate && dayjs(filters.startDate).isAfter(dayjs(filters.endDate))) {
      toast.error("Start Date cannot be after End Date.");
      return;
    }

    try {
      const requestFilters: Record<string, any> = {};

      if (schema.timeline) {
        let reqStartDate = filters.startDate;
        let reqEndDate = filters.endDate;

        // If dates are not provided, default to the past 1 year
        if (!reqStartDate && !reqEndDate) {
          reqEndDate = dayjs().format("YYYY-MM-DD");
          reqStartDate = dayjs().subtract(1, "year").format("YYYY-MM-DD");
        } else if (reqStartDate && !reqEndDate) {
          reqEndDate = dayjs(reqStartDate).add(1, "year").isAfter(dayjs())
            ? dayjs().format("YYYY-MM-DD")
            : dayjs(reqStartDate).add(1, "year").format("YYYY-MM-DD");
        } else if (!reqStartDate && reqEndDate) {
          reqStartDate = dayjs(reqEndDate).subtract(1, "year").format("YYYY-MM-DD");
        }

        requestFilters.startDate = reqStartDate;
        requestFilters.endDate = reqEndDate;

        if (schema.timeline.type === "MONTH_YEAR") {
          const startVal = dayjs(reqStartDate);
          requestFilters.month = startVal.month() + 1;
          requestFilters.year = startVal.year();
        }
      }

      if (filters.employeeIds && filters.employeeIds.length > 0) {
        requestFilters.employeeIds = filters.employeeIds;
      } else if (filters.employeeId) {
        requestFilters.employeeId = filters.employeeId;
      }
      if (filters.department) {
        requestFilters.department = filters.department;
      }

      // Dynamic custom filters
      if (schema.customFilters) {
        schema.customFilters.forEach((f) => {
          if (filters[f.key] !== undefined && filters[f.key] !== "") {
            requestFilters[f.key] = filters[f.key];
          }
        });
      }

      // Automatically include all hidden daily matrix columns (starting with day_)
      const hiddenDayColumns = columnsToRender
        .filter((col) => col.key.startsWith("day_"))
        .map((col) => col.key);
      const finalColumns = Array.from(new Set([...selectedColumns, ...hiddenDayColumns]));

      const blob = await downloadReport({
        moduleId: selectedModule,
        filters: requestFilters,
        columns: finalColumns,
      }).unwrap();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const filename = `${selectedModule}-report-${dayjs().format("YYYY-MM-DD")}.xlsx`;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`Successfully downloaded the ${schema.name}.`);
    } catch (err: any) {
      let errorMessage = "An unexpected error occurred during Excel generation.";
      if (err?.data instanceof Blob) {
        try {
          const text = await err.data.text();
          const parsed = JSON.parse(text);
          errorMessage = parsed.message || errorMessage;
        } catch {
          // ignore JSON parse errors
        }
      } else if (err?.data?.message) {
        errorMessage = err.data.message;
      } else if (err?.message) {
        errorMessage = err.message;
      }
      toast.error(errorMessage);
    }
  };

  return (
    <div className="p-6 px-12 mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-slate-900 mb-2">Dynamic Reports</h1>
        <p className="text-slate-500">
          Select a module, customize the timeframe, employees, and columns, and export directly to Excel.
        </p>
      </div>

      <Card className="shadow-md border border-slate-100 rounded-xl mb-6">
        <div className="mb-4">
          <label className="block text-sm font-semibold text-slate-700 mb-2">Select Report Module</label>
          <Select
            options={moduleOptions}
            value={selectedModule || ""}
            onChange={(val) => handleModuleChange(val as string)}
            placeholder="Select a module..."
            loading={loadingModules}
            clearable={false}
          />
        </div>

        {selectedModule && schema && (
          <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-100 flex items-center gap-2">
            <InfoCircleOutlined className="text-slate-400 flex-shrink-0" />
            <span className="text-sm text-slate-600 leading-normal">{schema.description}</span>
          </div>
        )}
      </Card>

      {isSchemaLoading ? (
        <DynamicReportsSkeleton />
      ) : (
        <div className={!selectedModule ? "opacity-60 pointer-events-none" : ""}>
            {/* Timeline / Timeframe Filter Group */}
            {(!selectedModule || schema?.timeline) && (
              <Card
                title={
                  <Space style={{ display: "flex", gap: "8px" }} className="flex items-center">
                    <CalendarOutlined />
                    <span>Timeframe Settings</span>
                  </Space>
                }
                className="shadow-md border border-slate-100 rounded-xl mb-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Start Date</label>
                    <DatePicker
                      value={filters.startDate ? dayjs(filters.startDate) : null}
                      onChange={(val) => handleFilterChange("startDate", val ? val.format("YYYY-MM-DD") : null)}
                      disabled={!selectedModule || downloading}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">End Date</label>
                    <DatePicker
                      value={filters.endDate ? dayjs(filters.endDate) : null}
                      onChange={(val) => handleFilterChange("endDate", val ? val.format("YYYY-MM-DD") : null)}
                      disabled={!selectedModule || downloading}
                      className="w-full"
                    />
                  </div>
                </div>
                <div className="mt-2 text-xs text-slate-400 italic">
                  *Leaving start and end dates blank will automatically generate the report for the past 1 year.
                </div>
              </Card>
            )}

            {/* Employees and Departments Filter Group */}
            {(!selectedModule || schema?.employees || schema?.departments || (schema?.customFilters && schema.customFilters.length > 0)) && (
              <Card
                title={
                  <Space style={{ display: "flex", gap: "8px" }} className="flex items-center">
                    <FilterOutlined />
                    <span>Data Scope Filters</span>
                  </Space>
                }
                className="shadow-md border border-slate-100 rounded-xl mb-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Standard Department Picker */}
                  {(!selectedModule || schema?.departments) && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Departments</label>
                      <Select
                        options={departmentOptions}
                        value={filters.department}
                        onChange={(val) => handleFilterChange("department", val as string)}
                        placeholder="All Departments"
                        disabled={!selectedModule || downloading}
                        searchable
                      />
                    </div>
                  )}

                  {/* Standard Employee Picker */}
                  {(!selectedModule || schema?.employees) && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Target Employees</label>
                      <Select
                        options={employeeOptions}
                        value={filters.employeeIds || []}
                        onChange={(val) => handleFilterChange("employeeIds", val as string[])}
                        placeholder="All Employees"
                        disabled={!selectedModule || downloading}
                        searchable
                        multiple
                      />
                    </div>
                  )}

                  {/* Custom/Extensible Schema-Driven Filters */}
                  {schema?.customFilters?.map((f) => {
                    const defaultAllLabel = f.label.toLowerCase().includes("status")
                      ? "All Statuses"
                      : f.label.toLowerCase().includes("type")
                      ? `All ${f.label}s`
                      : `All ${f.label}s`;

                    const hasAllOption = f.options?.some((opt) => opt.value === "");
                    const optionsWithAll = hasAllOption
                      ? f.options || []
                      : [{ value: "", label: defaultAllLabel }, ...(f.options || [])];

                    return (
                      <div key={f.key}>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">{f.label}</label>
                        <Select
                          options={optionsWithAll}
                          value={filters[f.key] || ""}
                          onChange={(val) => handleFilterChange(f.key, val as string)}
                          placeholder={defaultAllLabel}
                          disabled={!selectedModule || downloading}
                        />
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Column Selection Group */}
            <Card
              title={
                <Space style={{ display: "flex", gap: "8px" }} className="flex items-center">
                  <FolderOpenOutlined />
                  <span>Selected Columns to Include</span>
                </Space>
              }
              className="shadow-md border border-slate-100 rounded-xl mb-6"
            >
              <Checkbox.Group
                value={selectedColumns}
                onChange={handleColumnChange}
                className="w-full"
                disabled={!selectedModule || downloading}
              >
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  {visibleColumns.map((col) => (
                    <div key={col.key}>
                      <Checkbox value={col.key} disabled={col.locked || !selectedModule || downloading}>
                        {col.label}
                        {col.locked && (
                          <Tooltip title="This core column is locked and must be included in the report.">
                            <span className="text-slate-400 text-xs ml-1 font-normal">(Required)</span>
                          </Tooltip>
                        )}
                      </Checkbox>
                    </div>
                  ))}
                </div>
              </Checkbox.Group>
            </Card>

            {/* Trigger Button & Info Note */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mt-4">
              <div className="text-xs text-slate-400 flex items-center gap-0.5">
                <span className="font-semibold italic">*Note:</span>
                <span className="italic">Report generation may take some time, please be patient.</span>
              </div>
              <Button
                onClick={handleDownload}
                appearance="primary"
                size="large"
                loading={downloading}
                disabled={!selectedModule || downloading || !isFiltersValid}
              >
                <span className="flex items-center gap-2">
                  <FileExcelOutlined />
                  Generate And Download Excel Report
                </span>
              </Button>
            </div>
          </div>
        )}
    </div>
  );
};

// Help align Space icon vertical alignment inside Ant Design headers
const Space: React.FC<{ children: React.ReactNode; style?: React.CSSProperties; className?: string }> = ({
  children,
  style,
  className,
}) => (
  <div style={style} className={className}>
    {children}
  </div>
);

export default DynamicReports;
